import { useState } from 'react'
import { HiCheck }  from 'react-icons/hi'
import SectionTitle  from '../ui/SectionTitle'
import Button        from '../ui/Button'

// ─── Static comparison data ────────────────────────────────────────────────────
// Cell values: true = ✅, false = —, string = display text
const PACKAGES = [
  { key: 'small',   label: 'Small',   price: 'EGP 15,000' },
  { key: 'medium',  label: 'Medium',  price: 'EGP 30,000', popular: true },
  { key: 'premium', label: 'Premium', price: 'EGP 50,000' },
]

const ROWS = [
  { feature: 'Balloons',          small: '50',          medium: '100',         premium: '150'            },
  { feature: 'Cartoon Characters',small: '2',           medium: '3',           premium: '4'              },
  { feature: 'Cake',              small: '2kg',         medium: '3kg',         premium: '5kg + mini'     },
  { feature: 'Duration',          small: '3 hrs',       medium: '4 hrs',       premium: '5 hrs'          },
  { feature: 'Max Guests',        small: '20',          medium: '35',          premium: '50'             },
  { feature: 'DJ & Music',        small: true,          medium: true,          premium: true             },
  { feature: 'Puppet Show',       small: true,          medium: true,          premium: true             },
  { feature: 'Magician',          small: true,          medium: true,          premium: true             },
  { feature: 'Photography',       small: false,         medium: '50 photos',   premium: 'Full coverage'  },
  { feature: 'Video',             small: false,         medium: false,         premium: true             },
  { feature: 'Photo Booth',       small: false,         medium: false,         premium: true             },
  { feature: 'T-Shirt for Child', small: true,          medium: true,          premium: true             },
  { feature: 'VIP Gift Box',      small: false,         medium: false,         premium: true             },
]

// ─── Cell renderer ─────────────────────────────────────────────────────────────
function Cell({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10">
        <HiCheck size={16} className="text-primary" />
      </span>
    )
  }
  if (value === false) {
    return <span className="font-body text-ink-light text-lg">—</span>
  }
  return <span className="font-body text-ink text-sm font-semibold">{value}</span>
}

// ─── Component ─────────────────────────────────────────────────────────────────
export default function PackageComparison() {
  const [highlighted, setHighlighted] = useState(null)

  return (
    <section className="section-padding bg-gradient-card">
      <div className="container-custom">
        <SectionTitle
          eyebrow="Compare"
          title="What's Included"
          subtitle="See exactly what every package offers — hover or tap a column to highlight it."
        />

        {/* Horizontal-scroll wrapper (mobile) */}
        <div className="overflow-x-auto rounded-3xl shadow-card">
          <table className="w-full min-w-[640px] bg-surface">

            {/* ── Header ─────────────────────────────────────────────────── */}
            <thead>
              <tr>
                {/* Sticky feature column header */}
                <th
                  className="sticky left-0 z-10 bg-surface text-left p-5 font-body font-bold text-ink-muted text-sm border-b border-primary-100 w-44"
                  scope="col"
                >
                  Feature
                </th>

                {PACKAGES.map(({ key, label, price, popular }) => (
                  <th
                    key={key}
                    scope="col"
                    className={[
                      'p-5 text-center border-b border-primary-100 cursor-pointer select-none',
                      'transition-colors duration-200',
                      highlighted === key ? 'bg-primary/10' : 'hover:bg-primary/5',
                    ].join(' ')}
                    onMouseEnter={() => setHighlighted(key)}
                    onMouseLeave={() => setHighlighted(null)}
                    onClick={() => setHighlighted((h) => h === key ? null : key)}
                  >
                    <span
                      className={[
                        'font-display text-lg leading-tight transition-colors',
                        highlighted === key ? 'text-primary' : 'text-ink',
                      ].join(' ')}
                    >
                      {label}
                    </span>
                    {popular && (
                      <span className="block text-xs font-body font-bold text-primary mt-0.5">
                        ★ Most Popular
                      </span>
                    )}
                    <span className="block text-xs font-body text-ink-muted mt-1">{price}</span>
                  </th>
                ))}
              </tr>
            </thead>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <tbody>
              {ROWS.map(({ feature, ...values }, rowIdx) => (
                <tr
                  key={feature}
                  className={rowIdx % 2 === 0 ? 'bg-surface' : 'bg-background'}
                >
                  {/* Sticky feature label */}
                  <td
                    className={[
                      'sticky left-0 z-10 p-5 text-sm font-body font-semibold text-ink',
                      rowIdx % 2 === 0 ? 'bg-surface' : 'bg-background',
                    ].join(' ')}
                  >
                    {feature}
                  </td>

                  {PACKAGES.map(({ key }) => (
                    <td
                      key={key}
                      className={[
                        'p-5 text-center transition-colors duration-200',
                        highlighted === key ? 'bg-primary/8' : '',
                      ].join(' ')}
                      onMouseEnter={() => setHighlighted(key)}
                      onMouseLeave={() => setHighlighted(null)}
                    >
                      <Cell value={values[key]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

            {/* ── Footer CTA row ──────────────────────────────────────────── */}
            <tfoot>
              <tr className="border-t-2 border-primary/20 bg-gradient-card">
                <td className="sticky left-0 z-10 bg-gradient-to-r from-light-300 to-light-300 p-5" />
                {PACKAGES.map(({ key, label, popular }) => (
                  <td key={key} className="p-5 text-center">
                    <Button
                      to="/contact"
                      state={{ package: label }}
                      variant={popular ? 'primary' : 'secondary'}
                      size="sm"
                    >
                      Book {label}
                    </Button>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-center text-xs font-body text-ink-light mt-4">
          Tap a column header to highlight • Scroll horizontally on mobile
        </p>
      </div>
    </section>
  )
}
